import { getThumbWorkerClient, extractFramesViaWorker, isThumbWorkerAvailable } from './thumbWorkerClient';

export type StableThumbKey = string; // `${clipId}:${timeSampleMs}` - stable, never changes with zoom

export const THUMB_QUALITY_TIERS = {
  preview: 2.0,
  standard: 0.5,
  high: 0.25,
} as const;

export const stableThumbCache = new Map<StableThumbKey, string>();
const MAX_STABLE_THUMB_ENTRIES = 500;

/** Convert a canvas to a lightweight blob URL instead of a heavy base64 data URL. */
function canvasToBlobUrl(canvas: HTMLCanvasElement, quality = 0.7): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error('toBlob returned null'));
        resolve(URL.createObjectURL(blob));
      },
      'image/jpeg',
      quality,
    );
  });
}

function setStableThumb(key: StableThumbKey, value: string): void {
  if (stableThumbCache.size >= MAX_STABLE_THUMB_ENTRIES && !stableThumbCache.has(key)) {
    const firstKey = stableThumbCache.keys().next().value;
    if (firstKey !== undefined) {
      const oldValue = stableThumbCache.get(firstKey);
      stableThumbCache.delete(firstKey);
      if (oldValue && oldValue.startsWith('blob:')) {
        try { URL.revokeObjectURL(oldValue); } catch { /* noop */ }
      }
    }
  }
  stableThumbCache.set(key, value);
}
export const clipCacheStatus = new Map<
  string,
  { tier: keyof typeof THUMB_QUALITY_TIERS; srcUrl: string }
>();

function isR2SignedReadUrl(src: string): boolean {
  if (!src.includes('/r2/get')) return false;
  try {
    return new URL(src).pathname === '/r2/get';
  } catch {
    return false;
  }
}

/**
 * Derive a stable cache identity for a media src. For R2 signed URLs,
 * the URL changes on every remint (different `exp`/`sig`) so caching
 * by raw URL invalidates on every remint. Use the R2 object key (`k`
 * search param) instead — it's stable across remints. For all other
 * URLs (blob, data, Firebase Storage, HTTP) the URL itself is stable.
 */
function stableSrcKey(src: string): string {
  if (!src) return src;
  if (!src.includes('/r2/get')) return src;
  try {
    const u = new URL(src);
    if (u.pathname !== '/r2/get') return src;
    const k = u.searchParams.get('k');
    return k ? `r2:${k}` : src;
  } catch {
    return src;
  }
}

const makeStableKey = (clipId: string, timeSampleMs: number): StableThumbKey =>
  `${clipId}:${timeSampleMs}`;


export async function generateThumbs(
  src: string,
  mediaType: "video" | "image",
  inSec: number,
  outSec: number,
  count: number,
  frameW = 48,
  frameH = 28,
  signal?: AbortSignal
): Promise<string[]> {
  if (count < 1) count = 1;
  if (mediaType === "image") return new Array(count).fill(src);

  const thumbsDebug =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    window.localStorage.getItem("vb:editor2:thumbsDebug") === "1";
  if (thumbsDebug) console.log(`🎬 generateThumbs called for: ${src.substring(0, 60)}...`);

  const v = document.createElement("video");
  // Firebase Storage URLs fail CORS preflight if crossOrigin is set.
  // R2 Worker URLs explicitly emit CORS headers, so opt them into CORS mode.
  if (src.startsWith('blob:') || src.startsWith('data:') || isR2SignedReadUrl(src)) {
    v.crossOrigin = "anonymous";
  }
  v.muted = true;
  v.preload = "auto";
  v.src = src;
  try {
    await new Promise<void>((res, rej) => {
      if (signal?.aborted) return rej(new DOMException("aborted", "AbortError"));
      const onLoaded = () => {
        if (thumbsDebug) console.log(`✅ Video metadata loaded for thumbnail generation`);
        cleanup();
        res();
      };
      const onError = () => {
        cleanup();
        rej(new Error(`Video load error: ${src.substring(0, 50)}`));
      };
      const onAbort = () => {
        cleanup();
        rej(new DOMException("aborted", "AbortError"));
      };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", onLoaded);
        v.removeEventListener("error", onError);
        signal?.removeEventListener("abort", onAbort);
      };
      v.addEventListener("loadedmetadata", onLoaded, { once: true });
      v.addEventListener("error", onError, { once: true });
      signal?.addEventListener?.("abort", onAbort, { once: true });
    });

    const canvas = document.createElement("canvas");
    canvas.width = frameW;
    canvas.height = frameH;
    const ctx = canvas.getContext("2d")!;

    const imgs: string[] = [];
    for (let i = 0; i < count; i++) {
      if (signal?.aborted) throw new DOMException("aborted", "AbortError");
      const t = inSec + ((outSec - inSec) * i) / Math.max(1, count - 1);

      await new Promise<void>((res, rej) => {
        if (signal?.aborted) return rej(new DOMException("aborted", "AbortError"));
        const onseeked = () => {
          cleanup();
          res();
        };
        const onAbort = () => {
          cleanup();
          rej(new DOMException("aborted", "AbortError"));
        };
        const cleanup = () => {
          v.removeEventListener("seeked", onseeked);
          signal?.removeEventListener("abort", onAbort);
        };
        v.addEventListener("seeked", onseeked, { once: true });
        signal?.addEventListener?.("abort", onAbort, { once: true });
        v.currentTime = Math.max(0, t);
      });

      const vw = v.videoWidth || frameW;
      const vh = v.videoHeight || frameH;
      const scale = Math.max(frameW / vw, frameH / vh);
      const dw = Math.floor(vw * scale);
      const dh = Math.floor(vh * scale);
      const dx = Math.floor((frameW - dw) / 2);
      const dy = Math.floor((frameH - dh) / 2);

      ctx.clearRect(0, 0, frameW, frameH);
      ctx.drawImage(v, dx, dy, dw, dh);
      try {
        imgs.push(canvas.toDataURL("image/jpeg"));
      } catch {
        // SecurityError from tainted canvas (non-CORS URL) — skip this thumb
        imgs.push("");
      }
      // Yield to event loop between thumbnails to avoid blocking UI
      await new Promise(r => setTimeout(r, 0));
    }
    return imgs;
  } finally {
    try {
      v.pause();
      v.removeAttribute("src");
      v.load();
    } catch { /* cleanup best-effort */ }
  }
}

export async function generateStableThumbs(
  clipId: string,
  src: string,
  mediaType: "video" | "image",
  inSec: number,
  outSec: number,
  tier: keyof typeof THUMB_QUALITY_TIERS = "standard",
  signal?: AbortSignal,
  /** When true, skip the WebCodecs worker fast path which downloads the entire file into RAM. */
  skipWorkerFastPath?: boolean,
): Promise<void> {
  const interval = THUMB_QUALITY_TIERS[tier];
  const span = Math.max(0.001, outSec - inSec);
  const MAX_THUMB_SAMPLES = 120; // Cap to prevent memory/CPU issues on very long videos
  const sampleCount = Math.min(MAX_THUMB_SAMPLES, Math.max(2, Math.ceil(span / interval) + 1));

  const status = clipCacheStatus.get(clipId);
  const stableKey = stableSrcKey(src);
  if (status && status.tier === tier && status.srcUrl === stableKey) {
    return;
  }

  if (mediaType === "image") {
    for (let i = 0; i < sampleCount; i++) {
      const t = inSec + (span * i) / Math.max(1, sampleCount - 1);
      const timeSampleMs = Math.round(t * 1000);
      const key = makeStableKey(clipId, timeSampleMs);
      setStableThumb(key, src);
    }
    clipCacheStatus.set(clipId, { tier, srcUrl: stableKey });
    return;
  }

  // --- WebCodecs Worker fast path ---
  // Decode frames directly from the MP4 container in a Web Worker (no sequential seeks).
  // Falls through to legacy path on any failure.
  // SKIP for long videos: the fast path fetches the entire file into an ArrayBuffer which
  // causes OOM crashes for large files. The legacy sequential-seek path streams via HTMLVideoElement.
  if (!skipWorkerFastPath && isThumbWorkerAvailable() && (src.startsWith('blob:') || src.startsWith('http'))) {
    try {
      const response = await fetch(src);
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);
      const buffer = await response.arrayBuffer();

      const timestamps = Array.from({ length: sampleCount }, (_, i) => {
        const t = inSec + (span * i) / Math.max(1, sampleCount - 1);
        return Math.round(t * 1000);
      });

      const blobPromises: Promise<void>[] = [];
      await extractFramesViaWorker(
        buffer,
        timestamps,
        96,
        56,
        (timestampMs, bitmap) => {
          const key = makeStableKey(clipId, timestampMs);
          const canvas = document.createElement('canvas');
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
          const ctx2 = canvas.getContext('2d');
          if (ctx2) {
            ctx2.drawImage(bitmap, 0, 0);
            blobPromises.push(
              canvasToBlobUrl(canvas).then((blobUrl) => {
                setStableThumb(key, blobUrl);
              }).catch(() => { /* skip failed thumb */ }),
            );
          }
          bitmap.close();
        },
        signal,
      );
      await Promise.all(blobPromises);

      clipCacheStatus.set(clipId, { tier, srcUrl: stableKey });
      return;
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      // Fall through to legacy sequential-seek path
    }
  }
  // --- End Worker fast path ---

  const v = document.createElement("video");
  if (src.startsWith('blob:') || src.startsWith('data:') || isR2SignedReadUrl(src)) {
    v.crossOrigin = "anonymous";
  }
  v.muted = true;
  v.preload = "auto";
  v.src = src;
  try {
    await new Promise<void>((res, rej) => {
      if (signal?.aborted) return rej(new DOMException("aborted", "AbortError"));
      const onLoaded = () => {
        cleanup();
        res();
      };
      const onError = () => {
        cleanup();
        rej(new Error(`Video load error`));
      };
      const onAbort = () => {
        cleanup();
        rej(new DOMException("aborted", "AbortError"));
      };
      const cleanup = () => {
        v.removeEventListener("loadedmetadata", onLoaded);
        v.removeEventListener("error", onError);
        signal?.removeEventListener("abort", onAbort);
      };
      v.addEventListener("loadedmetadata", onLoaded, { once: true });
      v.addEventListener("error", onError, { once: true });
      signal?.addEventListener?.("abort", onAbort, { once: true });
    });

    const canvas = document.createElement("canvas");
    const frameW = 48;
    const frameH = 28;
    canvas.width = frameW;
    canvas.height = frameH;
    const ctx = canvas.getContext("2d")!;

    for (let i = 0; i < sampleCount; i++) {
      if (signal?.aborted) throw new DOMException("aborted", "AbortError");

      const t = inSec + (span * i) / Math.max(1, sampleCount - 1);
      const timeSampleMs = Math.round(t * 1000);
      const key = makeStableKey(clipId, timeSampleMs);

      if (stableThumbCache.has(key)) continue;

      await new Promise<void>((res, rej) => {
        if (signal?.aborted) return rej(new DOMException("aborted", "AbortError"));
        const onseeked = () => {
          cleanup();
          res();
        };
        const onAbort = () => {
          cleanup();
          rej(new DOMException("aborted", "AbortError"));
        };
        const cleanup = () => {
          v.removeEventListener("seeked", onseeked);
          signal?.removeEventListener("abort", onAbort);
        };
        v.addEventListener("seeked", onseeked, { once: true });
        signal?.addEventListener?.("abort", onAbort, { once: true });
        v.currentTime = Math.max(0, t);
      });

      // Try worker path first (offloads draw + JPEG encode off main thread)
      let rendered = false;
      try {
        const bitmap = await createImageBitmap(v);
        const client = getThumbWorkerClient();
        const dataUrl = await client.renderThumb(bitmap, frameW, frameH);
        if (dataUrl) {
          setStableThumb(key, dataUrl);
          rendered = true;
        }
      } catch {
        // Fallback to main-thread path (OffscreenCanvas not supported, or worker error)
      }

      if (!rendered) {
        const vw = v.videoWidth || frameW;
        const vh = v.videoHeight || frameH;
        const scale = Math.max(frameW / vw, frameH / vh);
        const dw = Math.floor(vw * scale);
        const dh = Math.floor(vh * scale);
        const dx = Math.floor((frameW - dw) / 2);
        const dy = Math.floor((frameH - dh) / 2);

        ctx.clearRect(0, 0, frameW, frameH);
        ctx.drawImage(v, dx, dy, dw, dh);
        try {
          const blobUrl = await canvasToBlobUrl(canvas);
          setStableThumb(key, blobUrl);
        } catch {
          // SecurityError from tainted canvas (non-CORS URL) — skip this thumb
        }
      }
      // Yield to event loop between thumbnails
      await new Promise(r => setTimeout(r, 0));
    }

    clipCacheStatus.set(clipId, { tier, srcUrl: stableKey });
  } finally {
    try {
      v.pause();
      v.removeAttribute("src");
      v.load();
    } catch { /* cleanup best-effort */ }
  }
}

export function getStableThumbsForClip(
  clipId: string,
  inSec: number,
  outSec: number,
  displayCount: number
): string[] {
  const span = Math.max(0.001, outSec - inSec);
  const safeCount = Number.isFinite(displayCount) ? Math.min(displayCount, 500) : 3;
  const result: string[] = [];

  for (let i = 0; i < safeCount; i++) {
    const t = inSec + (span * i) / Math.max(1, displayCount - 1);
    const timeSampleMs = Math.round(t * 1000);

    const key = makeStableKey(clipId, timeSampleMs);
    let thumb = stableThumbCache.get(key);

    if (!thumb) {
      let nearestKey: string | null = null;
      let nearestDist = Infinity;

      for (const [k] of stableThumbCache) {
        if (!k.startsWith(`${clipId}:`)) continue;
        const cachedMs = parseInt(k.split(":")[1], 10);
        const dist = Math.abs(cachedMs - timeSampleMs);
        if (dist < nearestDist) {
          nearestDist = dist;
          nearestKey = k;
        }
      }

      if (nearestKey) {
        thumb = stableThumbCache.get(nearestKey);
      }
    }

    result.push(thumb || "");
  }

  return result;
}

export async function getQuickFrame(
  src: string,
  t: number,
  w = 64,
  h = 36,
  pool: Map<string, HTMLVideoElement>
): Promise<string> {
  let v = pool.get(src);
  if (!v) {
    v = document.createElement("video");
    if (src.startsWith('blob:') || src.startsWith('data:') || isR2SignedReadUrl(src)) {
      v.crossOrigin = "anonymous";
    }
    v.muted = true;
    v.preload = "auto";
    v.src = src;
    pool.set(src, v);
    if (v.readyState < 1) {
      await new Promise<void>((res) =>
        v!.addEventListener("loadedmetadata", () => res(), { once: true })
      );
    }
  }

  await new Promise<void>((res) => {
    const on = () => {
      v!.removeEventListener("seeked", on);
      res();
    };
    v!.addEventListener("seeked", on, { once: true });
    v!.currentTime = Math.max(0, Math.min(v!.duration || t + 0.001, t));
  });

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  const vw = v.videoWidth || w;
  const vh = v.videoHeight || h;
  const s = Math.max(w / vw, h / vh);
  const dw = Math.floor(vw * s);
  const dh = Math.floor(vh * s);
  const dx = Math.floor((w - dw) / 2);
  const dy = Math.floor((h - dh) / 2);
  ctx.clearRect(0, 0, w, h);
  ctx.drawImage(v, dx, dy, dw, dh);
  return canvas.toDataURL("image/jpeg");
}
